import { MessageDescriptor } from 'react-intl';
import { SHORT_PLAIN_TEXT_50_LENGTH } from '../../../../../constants/common.constants';
import { FormVmItemConfigType, FormVmValueType, FormVmValueValidatorType } from '../../../../../infrastructure/form-viewmodel-controller';
import { maxCharactersValidator, requiredValidator, stringValueRequired } from '../../../../../modules/validators';
import { HIGH_PROFILE_ORDER_KEY, HIGH_PROFILE_REASONING_ATTENDEE_VALUE, HIGH_PROFILE_REASONING_KEY, isHighProfileReasoningRequired } from './order-field-visibility';
import {
    FIELD_CURRENCY_LABEL,
    FIELD_DESCRIPTION_LABEL,
    FIELD_PRICE_BOOK_LABEL,
    GROUP_BILLING,
    GROUP_DESCRIPTION,
    GROUP_INTEGRATION_DETAILS,
    GROUP_ORDER_DETAILS,
    GROUP_POST_EVENT,
    GROUP_PRODUCTS,
    HIGH_PROFILE_REASONING_REQUIRED_MESSAGE,
    ORDER_FIELD_GROUPS,
} from './salesforce-order.texts';

type AddOrderFieldType = 'select' | 'text' | 'textarea' | 'checkbox' | 'accountSearch' | 'opportunitySearch' | 'readOnlyLink' | 'placeholder';
type AddOrderOptionsSource = 'contracts' | 'multiEventOrders' | 'contacts' | 'pricebooks' | 'eventTypes';

interface IAddOrderField {
    key: string;
    label: MessageDescriptor;
    type: AddOrderFieldType;
    required?: boolean;
    fullWidth?: boolean;
    startsRow?: boolean;
    options?: string[];
    optionsSource?: AddOrderOptionsSource;
    inputType?: string;
    readOnly?: boolean;
    readOnlyOnCreate?: boolean;
    maxLength?: number;
    allowNone?: boolean;
    rows?: number;
}

interface IAddOrderGroup {
    title: MessageDescriptor;
    columns: number;
    fields: IAddOrderField[];
}

const LABELS_BY_KEY: Record<string, MessageDescriptor> = ORDER_FIELD_GROUPS
    .flatMap(group => group.fields)
    .reduce((acc, groupField) => {
        acc[groupField.key] = groupField.label;
        return acc;
    }, {} as Record<string, MessageDescriptor>);

const labelFor = (key: string): MessageDescriptor => LABELS_BY_KEY[key];

// Currency picklist (default USD).
export const CURRENCY_OPTIONS: string[] = ['AUD', 'CAD', 'EUR', 'GBP', 'HKD', 'JPY', 'KRW', 'USD'];
export const DEFAULT_CURRENCY = 'USD';

export const ADD_ORDER_OPTIONS: Record<string, string[]> = {
    orderStatus: ['New', 'In Progress', 'Cancelled', 'Delivered', 'Awaiting Confirmation', 'Complete'],
    billingStatus: [
        'Not Ready To Bill', 'Pending Billing Review', 'Ready To Bill', 'Invoice Sent',
        'Partially Paid', 'Paid', 'Cancelled',
    ],
    multiEventOrder: ['Single Order', 'Multi Event Order'],
    orderType: ['New Business', 'Existing Business', 'Existing Account - Expansion'],
    cancellationFee: ['Yes', 'No'],
    cancellationReason: [
        'Amended Price Book Change', 'Duplicate', 'Issuer No Longer Available', 'No Budget',
        'Postponed', 'Lost to Competition', 'Other', 'Test Record',
    ],
    billingMethod: [
        'Bill Monthly', 'Bill Per Event', 'Bill Against Deposit', 'Bill Quarterly', 'Bill Bi-Weekly',
        'Pre-bill', 'Hong Kong',
    ],
    qaChatEnabled: ['Yes', 'No'],
    qaReportSent: ['Yes', 'No'],
    eventRanSmoothly: ['Yes', 'No'],
    hybridEvent: ['In Discussion', 'No', 'Yes', 'Yes (OE Staff On-Site)', 'Yes (No OE Staff On-Site)'],
    currency: CURRENCY_OPTIONS,
    // Salesforce also has "Comeback Event" on this picklist, but it's set by automation, not manually by a PM here.
    highProfileReasoning: [
        'Client Flagship Event', 'Complex Hybrid Event', 'Complexity of Products/Delivery',
        'First Event with OpenExchange', HIGH_PROFILE_REASONING_ATTENDEE_VALUE, 'Redemption Event',
    ],
    accountLegalEntity: ['OpenExchange', 'OpenExchange HK', 'OpenExchange Korea', 'OpenExchange Inc, Limited', 'Veracast', 'N195'],
    isThisEventRepeatable: ['Yes', 'No'],
    lassoSyncStatus: ['Never Synced', 'Synced', 'Not Synced'],
    teamsCreated: ['Pending', 'Created', 'Failed'],
    billingTimeSensitivity: ['Within 72 Hours'],
};

export const ORDER_SUBTYPE_BY_TYPE: Record<string, string[]> = {
    'New Business': ['Acquisition', 'Win-Back'],
    'Existing Business': ['Retention'],
    'Existing Account - Expansion': ['New Division', 'New Event Type', 'Expanded Revenue'],
};

export const DEFAULT_ORDER_STATUS = 'New';
export const DEFAULT_BILLING_STATUS = 'Not Ready To Bill';
export const DEFAULT_HYBRID_EVENT = 'No';
export const DEFAULT_ACCOUNT_LEGAL_ENTITY = 'OpenExchange';
export const DEFAULT_LASSO_SYNC_STATUS = 'Never Synced';
export const DEFAULT_TEAMS_CREATED = 'Pending';

export const LOCKED_BILLING_STATUSES = new Set<string>([
    'Pending Billing Review', 'Ready To Bill', 'Invoice Sent', 'Partially Paid', 'Paid',
]);

export const LOCKED_FIELD_KEYS = new Set<string>([
    'billingMethod', 'billToLegalEntity', 'billingContact', 'billingContactEmail',
    'corporateRelated', 'billingNotes', 'billingTimeSensitivity', 'billingStatus', 'contractNumber',
    'clientCostCode', 'secondaryPO', 'additionalRecipient1', 'additionalRecipient2',
]);

const DEFAULT_VALUES: Record<string, string> = {
    orderStatus: DEFAULT_ORDER_STATUS,
    billingStatus: DEFAULT_BILLING_STATUS,
    currency: DEFAULT_CURRENCY,
    syncToTeams: 'Yes',
    hybridEvent: DEFAULT_HYBRID_EVENT,
    accountLegalEntity: DEFAULT_ACCOUNT_LEGAL_ENTITY,
    lassoSyncStatus: DEFAULT_LASSO_SYNC_STATUS,
    teamsCreated: DEFAULT_TEAMS_CREATED,
};

const field = (
    key: string,
    type: AddOrderFieldType,
    opts: {
        required?: boolean;
        fullWidth?: boolean;
        startsRow?: boolean;
        inputType?: string;
        optionsSource?: AddOrderOptionsSource;
        readOnly?: boolean;
        readOnlyOnCreate?: boolean;
        maxLength?: number;
        allowNone?: boolean;
        rows?: number;
    } = {},
): IAddOrderField => ({
    key: key,
    label: labelFor(key),
    type: type,
    required: opts.required,
    fullWidth: opts.fullWidth,
    startsRow: opts.startsRow,
    inputType: opts.inputType,
    optionsSource: opts.optionsSource,
    readOnly: opts.readOnly,
    readOnlyOnCreate: opts.readOnlyOnCreate,
    maxLength: opts.maxLength,
    allowNone: opts.allowNone,
    rows: opts.rows,
    options: type === 'select' && !opts.optionsSource ? ADD_ORDER_OPTIONS[key] : undefined,
});

export const ADD_ORDER_GROUPS: IAddOrderGroup[] = [
    {
        title: GROUP_ORDER_DETAILS,
        columns: 3,
        fields: [
            field('orderStatus', 'select', { required: true, readOnlyOnCreate: true }),
            field('billingStatus', 'select', { readOnlyOnCreate: true }),
            field('contractNumber', 'select', { required: true, optionsSource: 'contracts', allowNone: true }),
            field('multiEventOrder', 'select', { startsRow: true }),
            field('parentOrder', 'select', { optionsSource: 'multiEventOrders' }),
            field('requesterContact', 'select', { optionsSource: 'contacts' }),
            field('corporateRelated', 'accountSearch'),
            field('orderType', 'select'),
            field('orderSubtype', 'select'),
            field('eventType', 'select', { required: true, optionsSource: 'eventTypes' }),
            field('eventTypeClient', 'text', { maxLength: 255 }),
            field('channelAgencyReferral', 'accountSearch'),
            field('highProfileOrder', 'checkbox', { startsRow: true }),
            field('highProfileReasoning', 'select'),
            field('highProfileAttendeeDetails', 'textarea', { maxLength: 255 }),
            field('isThisEventRepeatable', 'select'),
            field('accountLegalEntity', 'select'),
            field('hybridEvent', 'select'),
            field('opportunity', 'opportunitySearch'),
            field('hostLob', 'text', { maxLength: SHORT_PLAIN_TEXT_50_LENGTH }),
            field('hostSubLob', 'text', { maxLength: SHORT_PLAIN_TEXT_50_LENGTH }),
            field('feedbackFormUrl', 'readOnlyLink'),
            field('cancellationReason', 'select'),
            field('cancellationFee', 'select'),
            field('cancellationOther', 'text', { fullWidth: true, maxLength: 255 }),
        ],
    },
    {
        title: GROUP_INTEGRATION_DETAILS,
        columns: 3,
        fields: [
            field('noStaffRequired', 'checkbox', { startsRow: true }),
            field('lassoSyncStatus', 'text', { readOnly: true }),
            field('lassoSyncDatetime', 'text', { readOnly: true }),
            field('syncToTeams', 'checkbox', { startsRow: true }),
            field('teamsCreated', 'text', { readOnly: true }),
            field('processedForArchive', 'checkbox', { readOnly: true }),
        ],
    },
    {
        title: GROUP_BILLING,
        columns: 2,
        fields: [
            field('billToLegalEntity', 'accountSearch', { fullWidth: true }),
            field('billingAddress', 'textarea', { fullWidth: true, readOnly: true }),
            field('billingContact', 'select', { optionsSource: 'contacts' }),
            field('billingContactEmail', 'text', { inputType: 'email', readOnly: true }),
            field('additionalRecipient1', 'select', { optionsSource: 'contacts' }),
            field('additionalRecipient1Email', 'text', { inputType: 'email', readOnly: true }),
            field('additionalRecipient2', 'select', { optionsSource: 'contacts' }),
            field('additionalRecipient2Email', 'text', { inputType: 'email', readOnly: true }),
            field('billingMethod', 'select'),
            field('clientCostCode', 'text'),
            field('secondaryPO', 'text'),
            field('billingTimeSensitivity', 'select'),
            field('billingNotes', 'textarea', { fullWidth: true, maxLength: 500 }),
        ],
    },
    {
        title: GROUP_POST_EVENT,
        columns: 3,
        fields: [
            field('qaChatEnabled', 'select'),
            field('qaReportSent', 'select'),
            field('eventRanSmoothly', 'checkbox'),
            field('postEventNotes', 'textarea', { fullWidth: true, maxLength: 1500, rows: 8 }),
        ],
    },
    {
        title: GROUP_DESCRIPTION,
        columns: 1,
        fields: [
            { key: 'description', label: FIELD_DESCRIPTION_LABEL, type: 'textarea', fullWidth: true, maxLength: 32000, rows: 8 },
        ],
    },
    {
        title: GROUP_PRODUCTS,
        columns: 2,
        fields: [
            { key: 'priceBook', label: FIELD_PRICE_BOOK_LABEL, type: 'text', readOnly: true },
            { key: 'currency', label: FIELD_CURRENCY_LABEL, type: 'text', readOnly: true },
        ],
    },
];

const ALL_FIELDS: IAddOrderField[] = ADD_ORDER_GROUPS.flatMap(group => group.fields);

const maxLengthValidator = (limit: number) =>
    (value: FormVmValueType) => (typeof value === 'string'
        ? maxCharactersValidator(value, limit)
        : { valid: true, validationMessage: null });

// Required only while highProfileOrder is checked, with the exact wording A/C 6 specifies (OEP-39923).
const highProfileReasoningRequiredValidator: FormVmValueValidatorType = (value, values = {}) => {
    if (!isHighProfileReasoningRequired(values[HIGH_PROFILE_ORDER_KEY])) {
        return { valid: true, validationMessage: null };
    }
    return stringValueRequired(value as string)
        ? { valid: true, validationMessage: null }
        : { valid: false, validationMessage: HIGH_PROFILE_REASONING_REQUIRED_MESSAGE };
};

export const ADD_ORDER_FORM_CONFIG: FormVmItemConfigType[] = ALL_FIELDS.map(f => ({
    name: f.key,
    labelDescriptor: f.label,
    defaultValue: DEFAULT_VALUES[f.key] ?? '',
    validators: f.key === HIGH_PROFILE_REASONING_KEY
        ? [highProfileReasoningRequiredValidator]
        : [
            ...(f.required ? [requiredValidator] : []),
            ...(f.maxLength ? [maxLengthValidator(f.maxLength)] : []),
        ],
}));

export { IAddOrderField, IAddOrderGroup, AddOrderFieldType, ALL_FIELDS };
